import { Button, notify, useConfirmModal } from '@affine/component';
import {
  SettingHeader,
  SettingWrapper,
} from '@affine/component/setting-components';
import { generateUrl } from '@affine/core/components/hooks/affine/use-share-url';
import { ServerService } from '@affine/core/modules/cloud';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import {
  type PublicDocLink,
  type PublicDocViewLink,
  PublicLinksService,
} from '@affine/core/modules/share-doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { PublicDocMode } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import * as styles from './styles.css';

const useNotifyError = () => {
  const t = useI18n();
  return useCallback(
    (error: unknown) => {
      notify.error({
        title: t['com.affine.public-view.error.title'](),
        message: UserFriendlyError.fromAny(error).message,
      });
    },
    [t]
  );
};

const useCopy = () => {
  const t = useI18n();
  const notifyError = useNotifyError();
  return useCallback(
    (url: string) => {
      navigator.clipboard
        .writeText(url)
        .then(() => notify.success({ title: t['Copied link to clipboard']() }))
        .catch(notifyError);
    },
    [notifyError, t]
  );
};

const useConfirmRevoke = () => {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  return useCallback(
    (onConfirm: () => Promise<void>) => {
      openConfirmModal({
        title: t['com.affine.public-view.revoke.confirm-title'](),
        description: t['com.affine.public-view.revoke.confirm-description'](),
        confirmText: t['com.affine.settings.workspace.public-links.revoke'](),
        cancelText: t['Cancel'](),
        confirmButtonOptions: { variant: 'error' },
        onConfirm,
      });
    },
    [openConfirmModal, t]
  );
};

const DocLinkRow = ({
  link,
  onOpen,
}: {
  link: PublicDocLink;
  onOpen: (docId: string) => void;
}) => {
  const t = useI18n();
  const publicLinks = useService(PublicLinksService);
  const serverService = useService(ServerService);
  const workspace = useService(WorkspaceService).workspace;
  const docDisplayMeta = useService(DocDisplayMetaService);
  const title = useLiveData(docDisplayMeta.title$(link.id));
  const copy = useCopy();
  const confirmRevoke = useConfirmRevoke();
  const notifyError = useNotifyError();

  const url = generateUrl({
    workspaceId: workspace.id,
    pageId: link.id,
    mode: link.mode === PublicDocMode.Edgeless ? 'edgeless' : 'page',
    baseUrl: serverService.server.baseUrl,
  });

  return (
    <div className={styles.row} data-testid="public-link-doc">
      <div className={styles.info}>
        <div className={styles.name} onClick={() => onOpen(link.id)}>
          {title || link.title}
        </div>
        <div className={styles.meta}>
          {t['com.affine.settings.workspace.public-links.views-count']({
            count: String(link.publicViewCount),
          })}
        </div>
      </div>
      <div className={styles.actions}>
        {url ? (
          <Button onClick={() => copy(url)}>
            {t['com.affine.settings.workspace.public-links.copy']()}
          </Button>
        ) : null}
        <Button
          variant="error"
          onClick={() =>
            confirmRevoke(() =>
              publicLinks.revokeDoc(link.id).catch(notifyError)
            )
          }
        >
          {t['com.affine.settings.workspace.public-links.revoke']()}
        </Button>
      </div>
    </div>
  );
};

const ViewLinkRow = ({
  link,
  onOpen,
}: {
  link: PublicDocViewLink;
  onOpen: (docId: string, blockId: string) => void;
}) => {
  const t = useI18n();
  const publicLinks = useService(PublicLinksService);
  const docDisplayMeta = useService(DocDisplayMetaService);
  const docTitle = useLiveData(docDisplayMeta.title$(link.docId));
  const copy = useCopy();
  const confirmRevoke = useConfirmRevoke();
  const notifyError = useNotifyError();
  const untitled = t['com.affine.settings.workspace.public-links.untitled']();

  return (
    <div className={styles.row} data-testid="public-link-view">
      <div className={styles.info}>
        <div
          className={styles.name}
          onClick={() => onOpen(link.docId, link.blockId)}
        >
          {link.tableTitle || untitled} › {link.viewName || untitled}
        </div>
        <div className={styles.meta}>
          {docTitle} ·{' '}
          {t['com.affine.settings.workspace.public-links.views-count']({
            count: String(link.viewCount),
          })}
        </div>
      </div>
      <div className={styles.actions}>
        <Button onClick={() => copy(publicLinks.viewLinkUrl(link.id))}>
          {t['com.affine.settings.workspace.public-links.copy']()}
        </Button>
        <Button
          variant="error"
          onClick={() =>
            confirmRevoke(() =>
              publicLinks.revokeView(link.id).catch(notifyError)
            )
          }
        >
          {t['com.affine.settings.workspace.public-links.revoke']()}
        </Button>
      </div>
    </div>
  );
};

/**
 * Settings → Public links: every doc and table view of the workspace that is
 * open to anyone with the link, with a way to revoke each of them.
 */
export const WorkspaceSettingPublicLinks = ({
  onCloseSetting,
}: {
  onCloseSetting: () => void;
}) => {
  const t = useI18n();
  const publicLinks = useService(PublicLinksService);
  const workbench = useService(WorkbenchService).workbench;
  const docs = useLiveData(publicLinks.docs$);
  const views = useLiveData(publicLinks.views$);

  useEffect(() => {
    publicLinks.revalidate().catch(console.error);
  }, [publicLinks]);

  const openDoc = useCallback(
    (docId: string, blockId?: string) => {
      workbench.openDoc(blockId ? { docId, blockIds: [blockId] } : docId);
      onCloseSetting();
    },
    [onCloseSetting, workbench]
  );

  return (
    <>
      <SettingHeader
        title={t['com.affine.settings.workspace.public-links']()}
        subtitle={t['com.affine.settings.workspace.public-links.description']()}
      />
      <SettingWrapper
        title={t['com.affine.settings.workspace.public-links.docs']()}
      >
        <div className={styles.list}>
          {docs.length === 0 ? (
            <div className={styles.empty}>
              {t['com.affine.settings.workspace.public-links.docs-empty']()}
            </div>
          ) : (
            docs.map(link => (
              <DocLinkRow key={link.id} link={link} onOpen={openDoc} />
            ))
          )}
        </div>
      </SettingWrapper>
      <SettingWrapper
        title={t['com.affine.settings.workspace.public-links.views']()}
      >
        <div className={styles.list}>
          {views.length === 0 ? (
            <div className={styles.empty}>
              {t['com.affine.settings.workspace.public-links.views-empty']()}
            </div>
          ) : (
            views.map(link => (
              <ViewLinkRow key={link.id} link={link} onOpen={openDoc} />
            ))
          )}
        </div>
      </SettingWrapper>
    </>
  );
};
